/**
 * What a person is actually agreeing to when they consent to being recorded.
 *
 * Bob, 2026-09-03: "you create the cinsent, look for what other major providers
 * are usinh and follow that formay and content so we are covered. ill have an
 * attorny review that at a later date, that review blocks nothing."
 *
 * FOLLOWS THE SHAPE THE MAJOR CONFERENCING PROVIDERS USE — Zoom, Google Meet and
 * Teams all disclose the same seven things in their recording notices, and it is
 * also, not coincidentally, close to what a GDPR Article 13 notice requires:
 *
 *   1. that recording is happening, and who is doing it
 *   2. what is captured
 *   3. why
 *   4. where it is stored
 *   5. how long it is kept
 *   6. who can see it
 *   7. what you can do about it
 *
 * An attorney reviewing this later will be checking it against that list, so it
 * is written as that list rather than as prose that has to be picked apart.
 *
 * THE FACTS COME FROM THE SERVER, NOT FROM HERE. Retention, capture mode and
 * storage location are configurable per deployment, and a notice that states
 * "90 days" while this deployment is set to 30 is not a smaller promise — it is
 * a false statement made to someone who has no way to check it. Every sentence
 * that asserts what we do takes its number from `disclosure`.
 *
 * VERSIONING IS LOAD-BEARING. The consent row stores the version alongside the
 * agreement, so a row can say WHICH wording the person saw. That only works if
 * the version changes whenever the wording does, which is why
 * `recordingConsentNotice.test.ts` fails the build when the text changes and the
 * version does not. The attorney's eventual edits are exactly the case this
 * protects: TermsReacceptGate re-prompts on a version change, so a later legal
 * revision reaches people who already agreed.
 */

export const RECORDING_CONSENT_VERSION = "v3";

export interface RecordingDisclosure {
  /** The window the retention sweeps apply, served from the same resolver they
   *  use (the org dial, then the founder dial `mtg_retention_days`, then the env
   *  default), so the notice cannot state a number the sweep does not apply. */
  retention_days: number;
  /** Whether this meeting belongs to a project. Project content is kept while
   *  the project is open and swept `retention_days` after it closes. A meeting
   *  with no project is swept `retention_days` after the meeting, unless
   *  something in a project was created from it (founder answer 1, #3616). */
  has_project: boolean;
  mode: string;
  storage: string;
  /** Whether recording is available on this deployment at all.
   *
   *  `RECORDING_ENABLED` is false in production, so `start_recording` returns a
   *  deliberate 403 — and the Record button offered it anyway, so a configured
   *  "off" reached the host as "Failed to start recording", which reads as a
   *  fault. Optional: an older server does not send it, and the button then
   *  behaves exactly as before rather than hiding itself on a guess. */
  recording_enabled?: boolean;
}

export interface NoticeSection {
  heading: string;
  body: string;
}

/** How the capture mode is described to someone who does not know our config. */
function captured(mode: string): string {
  switch (mode) {
    case "composite":
      return "the audio and video of everyone present, and anything shared on screen";
    case "track":
      return "the audio and video of each participant, recorded separately";
    default:
      return "the audio of everyone present";
  }
}

/** What happens to the COPIES of a project meeting's content. Founder answer 2
 *  (#3616 comment 5929041322), in his words: "The transcript copy in project
 *  documents (MEETING_TRANSCRIPT) is swept on the same clock as the transcript.
 *  Minutes, decisions and summaries (MEETING_MINUTES, meeting_artifacts) are
 *  kept as the project's business record." The sweep deletes the
 *  MEETING_TRANSCRIPT document and never the MEETING_MINUTES document or the
 *  meeting's decisions and summaries. */
export const COPIES =
  "The copy of the transcript in the project's documents is deleted on the same " +
  "schedule as the transcript. The copy of the minutes in the project's documents, " +
  "and the decisions and summaries drawn from the meeting, are kept as the " +
  "project's business record.";

export function recordingConsentNotice(d: RecordingDisclosure): NoticeSection[] {
  // THE RULE THE SWEEPS APPLY, stated per case (row 0d53b02b). A project
  // meeting's content lives as long as the project and is swept after it
  // closes. A meeting with no project (founder answer 1, #3616 comment
  // 5929041322): its recording is swept on its own age and its transcript and
  // minutes the same window after the meeting -- unless something in a project
  // was created from it, when the latest-closing such project's clock decides.
  const kept =
    d.retention_days <= 0
      ? "Recordings, transcripts and minutes are kept until someone deletes them. " +
        "This deployment has no automatic deletion configured."
      : d.has_project
        ? "Recordings, transcripts and minutes are kept while the project this " +
          "meeting belongs to is open, and deleted automatically " +
          `${d.retention_days} days after the project is closed. ` +
          COPIES
        : "This meeting is not part of a project. Its recording is deleted " +
          `automatically ${d.retention_days} days after it is made, and its ` +
          `transcript and minutes ${d.retention_days} days after the meeting. If ` +
          "anything in a project is created from this meeting, its transcript and " +
          "minutes are instead kept while that project is open and deleted " +
          `automatically ${d.retention_days} days after it is closed; if that is ` +
          "more than one project, the last one to close decides. The decisions and " +
          "summaries drawn from the meeting are kept.";

  return [
    {
      heading: "This meeting will be recorded",
      body:
        "Recording does not start until everyone this policy covers has agreed. " +
        "You can decline, and you can withdraw your agreement at any time.",
    },
    {
      heading: "What is captured",
      body:
        `We record ${captured(d.mode)}. We also produce a written transcript, ` +
        "and FTL may draw notes, decisions and follow-up actions from what is said.",
    },
    {
      heading: "Why",
      body:
        "So that what was agreed in this meeting becomes part of the project record " +
        "— decisions, requirements and actions — instead of depending on someone's memory.",
    },
    {
      heading: "Where it is stored, and for how long",
      body: `Recordings are stored in ${d.storage}. ${kept}`,
    },
    {
      heading: "Who can see it",
      body:
        "People in the organisation running this meeting who have access to the " +
        "project. It is not shared outside that organisation, and it is not used " +
        "to train anyone's models.",
    },
    {
      heading: "What you can do",
      body:
        "You can decline and stay in the meeting — recording will not start. You " +
        "can withdraw agreement later, which stops any further recording. To ask " +
        "for a recording of you to be deleted, or to ask what is held, contact the " +
        "person who organised this meeting.",
    },
  ];
}
